import { useEffect, useRef, useState } from "react";
import { Copy, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { adminApi } from "../api";
import { resolveImage } from "../../lib/data";
import { toast } from "sonner";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminApi.get("/admin/files").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await adminApi.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} file caricati`);
      load();
    } catch (err) {
      toast.error("Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere il file?")) return;
    await adminApi.delete(`/admin/files/${id}`);
    toast.success("File rimosso");
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiato");
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-start justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
            CMS
          </div>
          <h1 className="mt-2 font-display font-light text-5xl tracking-tightest">
            Media Library
          </h1>
          <p className="mt-3 text-white/60 max-w-xl">
            Carica e gestisci le immagini riutilizzabili su tutto il sito.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            onChange={upload}
            className="hidden"
            data-testid="media-upload-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm hover:bg-[#C48F56] disabled:opacity-50"
          >
            <Upload size={14} /> {uploading ? "Caricamento…" : "Carica immagini"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-white/10 p-16 text-center text-white/40">
            <ImageIcon size={28} className="inline" />
            <p className="mt-3 text-sm">Nessuna immagine caricata.</p>
          </div>
        )}
        {items.map((f) => (
          <div
            key={f.id}
            className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#0F0F14]"
            data-testid={`media-${f.id}`}
          >
            <div className="aspect-square relative">
              <img
                src={resolveImage(f.url)}
                alt={f.original_filename}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="p-3 text-xs">
              <div className="truncate" title={f.original_filename}>
                {f.original_filename}
              </div>
              <div className="mt-1 font-mono text-[10px] text-white/40">
                {(f.size / 1024).toFixed(0)} KB
              </div>
            </div>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => copy(f.url)}
                className="h-8 w-8 grid place-items-center rounded-full bg-black/70 backdrop-blur hover:bg-[#C48F56] hover:text-black"
                aria-label="Copia URL"
                title="Copia URL"
              >
                <Copy size={13} />
              </button>
              <button
                onClick={() => remove(f.id)}
                className="h-8 w-8 grid place-items-center rounded-full bg-black/70 backdrop-blur hover:bg-red-500"
                aria-label="Elimina"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
